import type { MatchTelemetryRow } from './Telemetry.js';

// V3 anonymous profile — a stable UUID stored in localStorage so the player has
// a persistent identity across sessions without any account or login.
// The UUID is NEVER sent to the server analytics pipeline (docs/13_PORTAL_CONSTRAINTS.md §8).

const UUID_KEY = 'signalclash.profile.uuid';
// V1.8 — return-visit anchor. Stored as ISO UTC date (YYYY-MM-DD) so we
// can render a "Welcome back — daily challenges have reset" hook on
// Day 2+ visits. The cheapest D1/D7 nudge that doesn't require any
// gameplay change.
const LAST_VISIT_KEY = 'signalclash.profile.lastVisit';
// V1.9 — consecutive-day login streak. Incremented when the new visit
// is exactly +1 day from the last; reset to 1 on a gap of 2+ days; left
// unchanged on a same-day re-open. Surfaced on the menu welcome banner
// so the player has a visible reason to come back tomorrow
// (CrazyGamesTricks §6.8).
const STREAK_KEY = 'signalclash.profile.streak';

export interface ProfileVisit {
  lastVisitUtc: string | null;
  daysSinceLastVisit: number;
  isReturning: boolean;
  streakDays: number;
  streakExtended: boolean;
}

export function loadProfile(): { uuid: string; lastVisitUtc: string | null } {
  return {
    uuid: getOrCreateUUID(),
    lastVisitUtc: readLastVisit(),
  };
}

// Returns the snapshot of the prior visit BEFORE writing the new one,
// so callers can render "Welcome back" against pre-visit state. If
// localStorage is unavailable we just return an empty record and skip
// the write — the welcome banner stays hidden, but the game still
// boots.
export function recordSessionVisit(today: string): ProfileVisit {
  const prior = readLastVisit();
  writeLastVisit(today);
  const priorStreak = readStreak();
  if (prior === null) {
    // First-ever session — streak begins at 1.
    writeStreak(1);
    return {
      lastVisitUtc: null,
      daysSinceLastVisit: 0,
      isReturning: false,
      streakDays: 1,
      streakExtended: false,
    };
  }
  const days = daysBetween(prior, today);
  let streakDays: number;
  let streakExtended = false;
  if (days === 0) {
    // Same-day re-open — leave the streak unchanged so refreshing the
    // tab doesn't pad the counter.
    streakDays = priorStreak > 0 ? priorStreak : 1;
  } else if (days === 1) {
    streakDays = (priorStreak > 0 ? priorStreak : 0) + 1;
    streakExtended = true;
  } else {
    // Missed a day or more — streak resets to today's visit.
    streakDays = 1;
  }
  writeStreak(streakDays);
  return {
    lastVisitUtc: prior,
    daysSinceLastVisit: days,
    isReturning: days >= 1,
    streakDays,
    streakExtended,
  };
}

export function readStreakDays(): number {
  return readStreak();
}

function readLastVisit(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(LAST_VISIT_KEY);
  } catch {
    return null;
  }
}

function writeLastVisit(value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LAST_VISIT_KEY, value);
  } catch {
    // Quota / privacy mode — non-fatal.
  }
}

function readStreak(): number {
  if (typeof localStorage === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (raw === null) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeStreak(value: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STREAK_KEY, String(Math.max(0, Math.floor(value))));
  } catch {
    // Quota / privacy mode — non-fatal.
  }
}

function daysBetween(aIso: string, bIso: string): number {
  const a = Date.parse(`${aIso}T00:00:00Z`);
  const b = Date.parse(`${bIso}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

export interface ProfileStats {
  uuid: string;
  totalMatches: number;
  wins: number;
  losses: number;
  doctrineStats: Record<string, { played: number; wins: number }>;
}

export function getOrCreateUUID(): string {
  if (typeof localStorage === 'undefined') return 'anon';
  try {
    const stored = localStorage.getItem(UUID_KEY);
    if (stored !== null) return stored;
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(UUID_KEY, id);
    return id;
  } catch {
    return 'anon';
  }
}

export function computeProfileStats(rows: readonly MatchTelemetryRow[]): ProfileStats {
  const uuid = getOrCreateUUID();
  const doctrineStats: Record<string, { played: number; wins: number }> = {};
  let wins = 0;
  for (const row of rows) {
    // V2 Tempo hot-fix: defensively handle stale rows whose `outcome` or
    // `doctrines` shape predates the current schema. Telemetry.isPlausibleRow
    // only validates 4 fields, so old rows can survive load with these
    // missing — touching `row.outcome.winner` directly crashes the menu.
    const outcome = (row as { outcome?: { winner?: unknown } }).outcome;
    const won = outcome !== undefined && outcome !== null && outcome.winner === 0;
    if (won) wins++;
    const doctrine = Array.isArray(row.doctrines) ? row.doctrines[0] ?? null : null;
    if (doctrine !== null) {
      if (!(doctrine in doctrineStats)) {
        doctrineStats[doctrine] = { played: 0, wins: 0 };
      }
      const entry = doctrineStats[doctrine];
      if (entry !== undefined) {
        entry.played++;
        if (won) entry.wins++;
      }
    }
  }
  return {
    uuid,
    totalMatches: rows.length,
    wins,
    losses: rows.length - wins,
    doctrineStats,
  };
}
